import { useState, useMemo } from 'react';
import { inferTableStructure } from '../../utils/tableInference';
import PreviewTable from './PreviewTable';

function BarMappingPanel({ columns, rows, initialMapping, chartId, onConfirm, onBack, ui }) {
  const numericColumns = columns.filter((c) => c.type === 'number');
  const stringColumns = columns.filter((c) => c.type === 'string');
  const labelCandidates = stringColumns.length > 0 ? stringColumns : columns;
  const inferred = useMemo(() => inferTableStructure(rows, columns, chartId), [rows, columns, chartId]);
  const isStacked = chartId === 'stacked-bar';
  const chartLabel = isStacked ? 'stacked bar' : 'grouped bar';
  const segmentLabel = isStacked ? 'stacked segment' : 'bar in each group';

  const initialMode = initialMapping.inputShape === 'wide' || Array.isArray(initialMapping.valueColumns) ? 'wide' : 'long';
  const [mode, setMode] = useState(initialMode);
  const [mapping, setMapping] = useState(initialMapping);

  const categoryValue = mapping.category || labelCandidates[0]?.name || '';
  const selectedValueColumns = Array.isArray(mapping.valueColumns) ? mapping.valueColumns : [];
  const selectedValue = mode === 'long' ? (mapping.value || '') : '';
  const selectedSeries = mapping.series || '';

  const canConfirm = mode === 'wide'
    ? Boolean(categoryValue) && selectedValueColumns.length > 0
    : Boolean(categoryValue) && Boolean(selectedValue);

  const toggleValueColumn = (name) => {
    setMapping((prev) => {
      const current = Array.isArray(prev.valueColumns) ? prev.valueColumns : [];
      return current.includes(name)
        ? { ...prev, valueColumns: current.filter((c) => c !== name) }
        : { ...prev, valueColumns: [...current, name], inputShape: 'wide', value: undefined, series: undefined };
    });
  };

  const handleConfirm = () => {
    if (mode === 'wide') {
      onConfirm({ inputShape: 'wide', category: categoryValue, valueColumns: selectedValueColumns });
    } else {
      onConfirm({
        inputShape: 'long',
        category: categoryValue,
        value: selectedValue,
        ...(selectedSeries ? { series: selectedSeries } : {}),
      });
    }
  };

  return (
    <div>
      {inferred?.notes && (
        <div className="border p-2.5 mb-3 text-xs leading-relaxed" style={{ color: ui.textSecondary, borderColor: ui.borderSecondary, backgroundColor: ui.bgSecondary }}>
          <span className="font-medium" style={{ color: ui.text }}>Auto-detected: </span>{inferred.notes}
        </div>
      )}
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <label className="text-xs" style={{ color: ui.text }}>
          <div className="mb-1 font-medium">Label column</div>
          <select
            value={categoryValue}
            onChange={(e) => setMapping((prev) => ({ ...prev, category: e.target.value }))}
            className="w-full text-xs border py-1 px-2"
            style={{ borderColor: ui.borderSecondary }}
          >
            <option value="">Select a column</option>
            {columns.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.type === 'number' ? '#' : 'abc'})
              </option>
            ))}
          </select>
        </label>
        <div className="text-xs" style={{ color: ui.text }}>
          <div className="mb-2 font-medium">How are your {isStacked ? 'segments' : 'groups'} organized?</div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setMode('wide')}
              className="flex-1 border p-3 text-left transition-colors"
              style={{
                borderColor: mode === 'wide' ? ui.border : ui.borderSecondary,
                backgroundColor: mode === 'wide' ? ui.bgSecondary : 'transparent'
              }}
            >
              <div className="font-medium mb-2" style={{ color: ui.text }}>Multiple Value Columns</div>
              <div className="text-[10px] mb-2" style={{ color: ui.textSecondary }}>Each column becomes a {segmentLabel}.</div>
              <table className="w-full text-[9px] border-collapse" style={{ borderColor: ui.borderSecondary }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: ui.borderSecondary }}>
                    <th className="text-left font-normal py-0.5">Label</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Group A</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Group B</th>
                  </tr>
                </thead>
                <tbody style={{ color: ui.textSecondary }}>
                  <tr>
                    <td className="py-0.5">Item 1</td>
                    <td className="py-0.5">10</td>
                    <td className="py-0.5">15</td>
                  </tr>
                  <tr>
                    <td className="py-0.5">Item 2</td>
                    <td className="py-0.5">12</td>
                    <td className="py-0.5">18</td>
                  </tr>
                </tbody>
              </table>
            </button>
            <button
              type="button"
              onClick={() => setMode('long')}
              className="flex-1 border p-3 text-left transition-colors"
              style={{
                borderColor: mode === 'long' ? ui.border : ui.borderSecondary,
                backgroundColor: mode === 'long' ? ui.bgSecondary : 'transparent'
              }}
            >
              <div className="font-medium mb-2" style={{ color: ui.text }}>Single Value Column</div>
              <div className="text-[10px] mb-2" style={{ color: ui.textSecondary }}>A category column groups the values.</div>
              <table className="w-full text-[9px] border-collapse" style={{ borderColor: ui.borderSecondary }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: ui.borderSecondary }}>
                    <th className="text-left font-normal py-0.5">Label</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Group</th>
                    <th className="text-left font-normal py-0.5" style={{ color: ui.text }}>Value</th>
                  </tr>
                </thead>
                <tbody style={{ color: ui.textSecondary }}>
                  <tr>
                    <td className="py-0.5">Item 1</td>
                    <td className="py-0.5">Group A</td>
                    <td className="py-0.5">10</td>
                  </tr>
                  <tr>
                    <td className="py-0.5">Item 1</td>
                    <td className="py-0.5">Group B</td>
                    <td className="py-0.5">15</td>
                  </tr>
                </tbody>
              </table>
            </button>
          </div>
        </div>
      </div>

      {mode === 'wide' ? (
        <div className="border p-3 mb-4" style={{ borderColor: ui.borderSecondary }}>
          <div className="text-xs font-medium mb-2" style={{ color: ui.text }}>Select columns — each becomes a {segmentLabel}</div>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {numericColumns.filter((c) => c.name !== categoryValue).map((c) => (
              <label key={c.name} className="flex items-center gap-2 text-xs" style={{ color: ui.text }}>
                <input type="checkbox" checked={selectedValueColumns.includes(c.name)} onChange={() => toggleValueColumn(c.name)} />
                <span>{c.name}</span>
              </label>
            ))}
          </div>
          {selectedValueColumns.length === 0 && (
            <p className="text-xs mt-2" style={{ color: ui.textSecondary }}>Select the numeric columns you want in the {chartLabel} chart.</p>
          )}
        </div>
      ) : (
        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <label className="text-xs" style={{ color: ui.text }}>
            <div className="mb-1 font-medium">Value column</div>
            <select
              value={selectedValue}
              onChange={(e) => setMapping((prev) => ({ ...prev, value: e.target.value, inputShape: 'long', valueColumns: undefined }))}
              className="w-full text-xs border py-1 px-2"
              style={{ borderColor: ui.borderSecondary }}
            >
              <option value="">Select a column</option>
              {numericColumns.filter((c) => c.name !== categoryValue).map((c) => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="text-xs" style={{ color: ui.text }}>
            <div className="mb-1 font-medium">Group by column</div>
            <select
              value={selectedSeries}
              onChange={(e) => setMapping((prev) => ({ ...prev, series: e.target.value }))}
              className="w-full text-xs border py-1 px-2"
              style={{ borderColor: ui.borderSecondary }}
            >
              <option value="">None</option>
              {columns.filter((c) => c.name !== categoryValue && c.type === 'string').map((c) => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <PreviewTable columns={columns} rows={rows} ui={ui} />
      <div className="flex gap-2">
        <button onClick={onBack} className="flex-1 text-xs border py-2 ui-hover ui-border" style={{ borderColor: ui.border, color: ui.text }}>&larr; Back</button>
        <button onClick={handleConfirm} disabled={!canConfirm} className={`flex-1 text-xs border py-2 font-medium ui-border ${!canConfirm ? 'opacity-40 cursor-not-allowed' : 'ui-hover'}`} style={{ borderColor: ui.border, color: ui.text }}>
          Confirm &rarr;
        </button>
      </div>
    </div>
  );
}

export default BarMappingPanel;
